import React, { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../state/store";
import { Check } from "../../components/Check/Check";
import { PlusMinusButton } from "../../components/PlusMinusButton";
import { Row } from "../../components/Row";
import * as skTypes from "../../types/sk";
import * as colors from "../../util/colors";
import { addSeasonToLocalStore, getLocalSeason } from "../../util/skUtils";
import { v4 as uuid } from "uuid";
import { useRequestHandler } from "../../hooks/requestHandler";
import { useDataProvider } from "../../hooks/dataProvider";
import { useKeyedParams } from "../../hooks/useParams";
import {
  addTeamSet,
  getImportableTeamSets,
  getSeasonInfo,
  setSelectedSeason,
  updateSeason,
} from "../../state/actions/skActions";
import "./SK.scss";
import { SimpleSelection, StringInput } from "../../components/Inputs";
import { arrayToEnum } from "../../util/generalUtil";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";

interface EditTeamsProps {
  seasonId: string;
}

export function EditTeams() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { seasonId } = useKeyedParams<EditTeamsProps>();
  const { selectedEvent } = useAppSelector((state) => state.sk);
  useDocumentTitle("Teams - BQA Scorekeeper");
  const { user } = useAppSelector((state) => state.authentication);

  const getDefaultTeam = (): skTypes.Team => {
    return {
      teamName: "",
      teamId: uuid(),
      quizzers: [
        {
          quizzerName: "",
          quizzerId: uuid(),
          active: true,
        },
      ],
      active: true,
    };
  };

  const [teamData, setTeamData] = useState<skTypes.Team>(getDefaultTeam());
  const [confirmingDelete, setConfirmingDelete] = useState<boolean>(false);

  const [selectedTeam, setSelectedTeam] = useState<number>(-1);
  const [importing, setImporting] = useState<boolean>(false);
  const [importableTeams, setImportableTeams] = useState<
    {
      setName: string;
      setId: string;
      lastUpdated: string;
      teamCount: number;
      changeLog?: string[];
    }[]
  >([]);
  const [confirmImport, setConfirmImport] = useState<string>("");
  const setToImport = importableTeams.find(
    (theSet) => theSet.setId === confirmImport
  );

  // Creating and editing and deleting
  const [creatingNewTeam, setCreatingNewTeam] = useState<boolean>(false);
  const [teamEditing, setTeamEditing] = useState<number>(-1); // If -1, creating
  const [lineupsEditing, setLineupsEditing] = useState<boolean>(false);
  const [lineupIndex, setLineupIndex] = useState<number>(-1); // If -1, creating
  const [lineupData, setLineupData] = useState<skTypes.Lineup[]>([]);

  const [lineupQuizzerSelected, setLineupQuizzerSelected] = useState<
    number | undefined
  >();

  const handler = useRequestHandler({
    onSuccess: (key) => {
      switch (key) {
        case "IMPORT":
          navigate(`/sk/${seasonId}/events`);
          break;
        case "LINEUP":
          setLineupsEditing(false);
      }
    },
    assumeCachedInfo: true,
  });
  const provider = useDataProvider({
    getData: getSeasonInfo,
    selector: (state) => state.sk.selectedSeason,
    dispatchAction: setSelectedSeason,
    id: seasonId,
    getId: (season) => season.seasonId,
    fallbackUrl: "/sk/seasons",
    cacheDetails: {
      prefix: "season",
      get: getLocalSeason,
      save: addSeasonToLocalStore,
    },
    render: (selectedSeason) => {
      const getLineupQuizzerList = () => {
        if (lineupIndex === -1) return null;
        const getQuizzerRow = (
          quizzer: skTypes.Quizzer,
          index: number
        ): React.ReactElement => {
          return (
            <div
              style={{ display: "flex", height: 32, marginBottom: 2 }}
              key={quizzer.quizzerId}
            >
              <button
                style={{
                  width: 20,
                  color: lineupData[lineupIndex].captain === index ? "red" : "",
                  cursor: "pointer",
                  background: "none",
                  fontSize: 16,
                }}
                onClick={() => {
                  if (index >= 5) return;
                  const newLineups = [...lineupData];
                  newLineups[lineupIndex] = {
                    ...lineupData[lineupIndex],
                    captain:
                      lineupData[lineupIndex].captain === index
                        ? undefined
                        : index,
                  };
                  setLineupData(newLineups);
                }}
              >{`${index < 5 ? index + 1 : "S"}.`}</button>
              <button
                style={{
                  backgroundColor:
                    lineupQuizzerSelected === index
                      ? colors.bqaBlue
                      : "transparent",
                  minWidth: 150,
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: 16,
                }}
                onClick={() => {
                  if (lineupQuizzerSelected !== undefined) {
                    const newLineup = {
                      ...lineupData[lineupIndex],
                      quizzerIdList: [...lineupData[lineupIndex].quizzerIdList],
                    };
                    newLineup.quizzerIdList[index] =
                      newLineup.quizzerIdList[lineupQuizzerSelected];
                    newLineup.quizzerIdList[lineupQuizzerSelected] =
                      quizzer.quizzerId;

                    const newList = [...lineupData];
                    newList[lineupIndex] = newLineup;

                    setLineupData(newList);
                    setLineupQuizzerSelected(undefined);
                  } else {
                    setLineupQuizzerSelected(index);
                  }
                }}
              >
                {quizzer.quizzerName}
              </button>
            </div>
          );
        };
        const result = lineupData[lineupIndex].quizzerIdList.map(
          (quizzerId, index) => {
            const quizzer: skTypes.Quizzer = selectedSeason.teams[
              selectedTeam
            ].quizzers.find(
              (quizzerObj: skTypes.Quizzer) =>
                quizzerObj.quizzerId === quizzerId
            ) as skTypes.Quizzer;
            return getQuizzerRow(quizzer, index);
          }
        );
        selectedSeason.teams[selectedTeam].quizzers.forEach(
          (quizzer: skTypes.Quizzer) => {
            if (
              !lineupData[lineupIndex].quizzerIdList.includes(
                quizzer.quizzerId
              ) &&
              quizzer.active
            )
              result.push(getQuizzerRow(quizzer, result.length));
          }
        );
        return result;
      };

      const getTeamListDisplay = (): React.ReactNode[] => {
        const result: React.ReactNode[] = [];
        selectedSeason?.teams.forEach((team: skTypes.Team, index: number) => {
          result.push(
            <div
              key={`team-${team.teamId}`}
              className={`season-event-list ${
                index === selectedTeam ? "season-event-list-active" : ""
              }`}
              style={{ fontWeight: "bold", fontSize: 18, width: "unset" }}
              onClick={() => {
                setSelectedTeam(index);
                setConfirmingDelete(false);
                setLineupsEditing(false);
                window.scrollTo(0, 0);
              }}
            >
              {team.teamName}
            </div>
          );
        });
        return result;
      };
      const getSelectedTeamDisplay = (): React.ReactNode[] => {
        const result: React.ReactNode[] = [];
        selectedSeason?.teams[selectedTeam]?.quizzers.forEach(
          (quizzer: skTypes.Quizzer) => {
            if (quizzer.active) {
              result.push(
                <div
                  key={`quizzer-${quizzer.quizzerId}`}
                  style={{ fontSize: 18 }}
                >
                  {quizzer.quizzerName}
                </div>
              );
            }
          }
        );
        return result;
      };
      const editFormVisible: boolean =
        (teamEditing >= 0 || creatingNewTeam) && !importing;

      if (importing) {
        return (
          <div className="page">
            {handler.snackbar}
            <Row>
              <button
                className="secondary-btn"
                style={{ marginRight: 10 }}
                onClick={() => {
                  setImporting(false);
                }}
                key="cancel"
              >
                Cancel
              </button>
              <h1>Import Team Set</h1>
            </Row>
            <p>
              Click on any of the team sets below to add them to your team
              directory for this season.
            </p>
            {selectedSeason?.importedTeams && (
              <p>
                <span className="important-information">{"NOTE: "}</span>
                {`You are already subscribed to ${
                  selectedSeason.importedTeams.setName ||
                  selectedSeason.importedTeams.setId
                }. Importing a set a
              second time will keep all your stats, but will override your existing
              teams.`}
              </p>
            )}
            {confirmImport && (
              <div style={{ marginTop: 5, marginBottom: 20 }}>
                <button
                  className="link"
                  onClick={() => {
                    if (handler.requestInProgress || !setToImport) return;
                    handler.runRequest(
                      addTeamSet(seasonId, setToImport.setId),
                      "Adding this team set...",
                      "Team set added!",
                      "IMPORT"
                    );
                  }}
                >
                  <span className="important-information">CONFIRM:</span>&nbsp;
                  {`Import Team Set ${setToImport?.setName || ""}`}
                </button>
                <br />
                <br />
                <button className="link" onClick={() => setConfirmImport("")}>
                  Cancel
                </button>
              </div>
            )}
            {!confirmImport &&
              importableTeams
                .filter(
                  (set) =>
                    !selectedSeason?.importedTeams ||
                    set.setId === selectedSeason.importedTeams.setId
                )
                .map((teamSet, index) => (
                  <div
                    key={teamSet.setId}
                    className="season-event-list"
                    onClick={() => {
                      setConfirmImport(teamSet.setId);
                    }}
                    style={{
                      marginBottom: 10,
                      paddingBottom: 10,
                      minWidth: 300,
                      height: "auto",
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: "bold", fontSize: 16 }}>
                        {teamSet.setName}
                      </span>
                      <span
                        style={{ fontWeight: "normal", fontSize: 14 }}
                      >{`: ${teamSet.teamCount} team${
                        teamSet.teamCount > 1 ? "s" : ""
                      }`}</span>
                    </div>
                    {teamSet.changeLog && (
                      <ul style={{ marginLeft: 5, marginTop: 0 }}>
                        {teamSet.changeLog.map((item: string) => (
                          <li key={item} style={{ fontSize: 12 }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
          </div>
        );
      }

      return (
        <div className="page" style={{ padding: 20 }}>
          {handler.snackbar}
          <Row>
            {!editFormVisible && (
              <Link
                className="secondary-btn"
                style={{ marginRight: 10 }}
                to={`/sk/${seasonId}/${selectedEvent || "events"}`}
              >
                Back to Season
              </Link>
            )}
            <h1>Teams</h1>
          </Row>
          <p>
            <span style={{ marginRight: 20 }}>
              {`Season: ${selectedSeason?.seasonName}`}
            </span>
          </p>
          <Row>
            {!editFormVisible && user && (
              <button
                className="clickable"
                style={{ marginRight: 20 }}
                onClick={async () => {
                  const newImportableTeams = await getImportableTeamSets();
                  setImporting(true);
                  setConfirmImport("");
                  setImportableTeams(newImportableTeams);
                }}
              >
                Import Team Set
              </button>
            )}
            {editFormVisible ? (
              <button
                className="secondary-btn"
                key="create-cancel"
                onClick={() => {
                  setCreatingNewTeam(false);
                  setTeamEditing(-1);
                }}
              >
                Cancel
              </button>
            ) : (
              <button
                className="clickable"
                key="create"
                onClick={() => {
                  setCreatingNewTeam(true);
                  setTeamData(getDefaultTeam());
                }}
              >
                Create New Team
              </button>
            )}
          </Row>
          {editFormVisible ? (
            <div>
              <h3 style={{ fontSize: 14, fontWeight: "bold" }}>
                {creatingNewTeam ? "Create Team" : "Edit Team"}
              </h3>
              <StringInput
                caption="Team Name:"
                value={teamData.teamName || ""}
                onChange={(value) =>
                  setTeamData({ ...teamData, teamName: value })
                }
                data-testid="sk-teams-name"
              />
              <StringInput
                caption="Nickname"
                value={teamData.nickname || ""}
                onChange={(value) =>
                  setTeamData({ ...teamData, nickname: value })
                }
              />
              <StringInput
                caption="Coach"
                value={teamData.coach || ""}
                onChange={(value) => setTeamData({ ...teamData, coach: value })}
              />
              {!creatingNewTeam && (
                <Check
                  checked={teamData.active}
                  style={{ width: 200, marginTop: 10 }}
                  onClick={(value: boolean) =>
                    setTeamData({ ...teamData, active: value })
                  }
                >
                  Active
                </Check>
              )}
              <p>Quizzers</p>
              {teamData.quizzers.map(
                (quizzer: skTypes.Quizzer, index: number) => (
                  <StringInput
                    key={quizzer.quizzerId}
                    caption={`${index + 1}. Quizzer Name:`}
                    value={quizzer.quizzerName || ""}
                    onChange={(value) => {
                      const newTeamData = {
                        ...teamData,
                        quizzers: [...teamData.quizzers],
                      };
                      newTeamData.quizzers[index].quizzerName = value;
                      setTeamData(newTeamData);
                    }}
                    spacing={false}
                    afterInput={
                      <>
                        {!creatingNewTeam ? (
                          <Check
                            checked={quizzer.active}
                            onClick={(value: boolean) => {
                              const newTeamData = {
                                ...teamData,
                                quizzers: [...teamData.quizzers],
                              };
                              newTeamData.quizzers[index].active = value;
                              setTeamData(newTeamData);
                            }}
                            style={{
                              marginTop: -4,
                              height: 35,
                              width: 120,
                              marginRight: 10,
                            }}
                          >
                            Active
                          </Check>
                        ) : teamData.quizzers.length > 1 ? (
                          <PlusMinusButton
                            onClick={() => {
                              const newTeamData = {
                                ...teamData,
                                quizzers: teamData.quizzers.filter(
                                  (_, i) => i !== index
                                ),
                              };
                              setTeamData(newTeamData);
                            }}
                            ariaLabel="Remove Quizzer"
                            style={{ marginRight: 10 }}
                          >
                            -
                          </PlusMinusButton>
                        ) : null}
                        <PlusMinusButton
                          onClick={() => {
                            const newTeamData = {
                              ...teamData,
                              quizzers: [...teamData.quizzers],
                            };
                            newTeamData.quizzers.push({
                              quizzerId: uuid(),
                              quizzerName: "",
                              active: true,
                            });
                            setTeamData(newTeamData);

                            const newElement = document.getElementById(
                              `team-quizzer-${newTeamData.quizzers.length - 1}`
                            );
                            if (newElement) newElement.focus();
                          }}
                          style={{
                            visibility:
                              index === teamData.quizzers.length - 1
                                ? "visible"
                                : "hidden",
                          }}
                          ariaLabel="Add Quizzer"
                        >
                          +
                        </PlusMinusButton>
                      </>
                    }
                  />
                )
              )}
              <button
                className="clickable"
                style={{ marginTop: 10 }}
                onClick={() => {
                  if (!teamData.teamName) return;
                  if (selectedSeason) {
                    const newSeason = {
                      ...selectedSeason,
                      teams: [...selectedSeason.teams],
                    };
                    if (creatingNewTeam) {
                      newSeason.teams.push(teamData);
                      setCreatingNewTeam(false);
                    } else {
                      newSeason.teams[teamEditing] = teamData;
                      setTeamEditing(-1);
                    }
                    dispatch(updateSeason(newSeason, user !== undefined));
                  }
                }}
              >
                Save Team
              </button>
            </div>
          ) : (
            <>
              <p key="message" className="important-information">
                {selectedSeason.teams.length === 0
                  ? "You don't have any teams yet. Create a new one above or, if you are logged in, import a pre-defined set."
                  : "Click a team name to edit it, create a new one above, or import a set."}
              </p>
              <div style={{ display: "flex", marginTop: 20 }}>
                {!(selectedTeam >= 0) && (
                  <div style={{ width: 300 }}>{getTeamListDisplay()}</div>
                )}
                {selectedTeam >= 0 && (
                  <>
                    <div style={{ fontSize: 16, minWidth: 180 }}>
                      <button
                        className="secondary-btn"
                        onClick={() => setSelectedTeam(-1)}
                      >
                        Back to Team List
                      </button>
                      <div style={{ fontSize: 24, marginTop: 20 }}>
                        {selectedSeason?.teams[selectedTeam]?.teamName}
                      </div>
                      {selectedSeason?.teams[selectedTeam]?.coach && (
                        <div style={{ marginBottom: 10 }}>
                          {`Coach: ${selectedSeason?.teams[selectedTeam]?.coach}`}
                        </div>
                      )}
                      {!selectedSeason?.teams[selectedTeam]?.active && (
                        <div
                          className="important-information"
                          style={{ marginBottom: 10 }}
                        >
                          Not Active
                        </div>
                      )}
                      {getSelectedTeamDisplay()}

                      <button
                        className="link"
                        style={{ margin: "15px 0px", paddingLeft: 0 }}
                        onClick={() => {
                          if (selectedSeason) {
                            setTeamEditing(selectedTeam);
                            setTeamData(selectedSeason.teams[selectedTeam]);
                            setLineupsEditing(false);
                          }
                        }}
                      >
                        Edit Team
                      </button>
                      <br />
                      <button
                        className="link"
                        style={{ marginBottom: 15, paddingLeft: 0 }}
                        onClick={() => {
                          if (selectedSeason) {
                            setLineupsEditing(true);
                            const lineups =
                              selectedSeason.teams[selectedTeam]
                                .presetLineups || [];
                            setLineupData(lineups);
                            setLineupIndex(lineups.length >= 1 ? 0 : -1);
                          }
                        }}
                      >
                        Edit Preset Lineups
                      </button>
                      <br />
                      <button
                        className="link"
                        style={{ paddingLeft: 0 }}
                        onClick={() => {
                          if (!confirmingDelete) {
                            setConfirmingDelete(true);
                            return;
                          }
                          const newSeason: skTypes.Season = {
                            ...selectedSeason,
                            teams: [...(selectedSeason?.teams || [])],
                          } as skTypes.Season;
                          newSeason.teams.splice(selectedTeam, 1);
                          handler.runRequest(
                            updateSeason(newSeason, user !== undefined),
                            "Deleting the team...",
                            "Team deleted!"
                          );
                          setSelectedTeam(-1);
                          setConfirmingDelete(false);
                        }}
                      >
                        {confirmingDelete
                          ? "Confirm Delete Team!?"
                          : "Delete Team"}
                      </button>
                    </div>
                    {lineupsEditing && (
                      <div style={{ marginLeft: 30 }}>
                        <h3 style={{ marginTop: 0 }}>Edit Lineups</h3>
                        <p>
                          Make sure you provide a lineup name. Once you are
                          done, click Save Lineups below.
                        </p>
                        <SimpleSelection
                          caption="Lineup Name"
                          value={String(lineupIndex)}
                          onChange={(e) => {
                            const index = Number.parseInt(e);
                            setLineupIndex(index);
                          }}
                          values={{
                            "-1": "Create New Lineup",
                            ...arrayToEnum(lineupData, (lineup, index) => ({
                              key: String(index),
                              value: lineup.lineupName,
                            })),
                          }}
                        />
                        <StringInput
                          caption="Lineup Name: "
                          style={{ marginTop: 10, marginBottom: 10 }}
                          value={lineupData[lineupIndex]?.lineupName || ""}
                          spacing={false}
                          onChange={(value) => {
                            const team = selectedSeason.teams[selectedTeam];
                            if (lineupIndex === -1) {
                              setLineupData([
                                ...lineupData,
                                {
                                  lineupName: value,
                                  quizzerIdList: team.quizzers
                                    .filter((q) => q.active)
                                    .map((q) => q.quizzerId),
                                },
                              ]);
                              setLineupIndex(lineupData.length);
                            } else {
                              const newLineups = [...lineupData];
                              newLineups[lineupIndex] = {
                                ...newLineups[lineupIndex],
                                lineupName: value,
                              };
                              setLineupData(newLineups);
                            }
                          }}
                        />
                        {getLineupQuizzerList()}
                        {lineupIndex >= 0 && (
                          <button
                            className="link"
                            style={{ marginTop: 10 }}
                            onClick={() => {
                              const newLineups = [...lineupData];
                              newLineups.splice(lineupIndex, 1);
                              setLineupData(newLineups);
                              setLineupIndex(-1);
                            }}
                          >
                            Delete Lineup
                          </button>
                        )}
                        <div>
                          <button
                            className="clickable"
                            style={{ marginTop: 20 }}
                            onClick={() => {
                              if (!selectedSeason) return;
                              const newSeason = {
                                ...selectedSeason,
                                teams: [...selectedSeason.teams],
                              };
                              const team = {
                                ...selectedSeason.teams[selectedTeam],
                                presetLineups: lineupData,
                              };
                              newSeason.teams[selectedTeam] = team;
                              handler.runRequest(
                                updateSeason(newSeason, user !== undefined),
                                "Saving lineups...",
                                "Lineups saved!",
                                "LINEUP"
                              );
                            }}
                          >
                            Save Lineups
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      );
    },
  });

  return provider.getPage();
}
